import {
  Box,
  ButtonProps,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Skeleton,
  useTheme,
} from "@mui/material";
import {
  BaseCard,
  ErrorCard,
  Fields,
  FieldsData,
  LoadingCard,
  StatusQueryState,
  Typography,
} from "@platform/design-system";
import { PropsWithChildren, ReactNode, useEffect, useState } from "react";

import type { MappedCoverage } from "@platform/api";

type Data = {
  currentCoverages: MappedCoverage[];
  newCoverages: MappedCoverage[];
};

type Props = {
  retry: ButtonProps["onClick"];
} & StatusQueryState<Data>;

const CoveragesCard = (props: Props): React.JSX.Element => {
  if (props.status === "pending") {
    return <CoveragesLoading />;
  }

  if (props.status === "error") {
    return (
      <ErrorCard
        title="Error loading coverages"
        error={props.error}
        retry={{ onClick: props.retry }}
      />
    );
  }

  if (
    props.data.currentCoverages.length === 0 &&
    props.data.newCoverages.length === 0
  ) {
    return (
      <ErrorCard
        title="Error"
        error={new Error("Coverages failed to load")}
        retry={{ onClick: props.retry }}
      />
    );
  }

  return <CoveragesData data={props.data} />;
};

function CoveragesData({ data }: { data: Data }): React.JSX.Element {
  const [selectedCoverage, setSelectedCoverage] =
    useState<MappedCoverage | null>(null);

  useEffect(() => {
    setSelectedCoverage(data.currentCoverages[0] || data.newCoverages[0]);
  }, [data]);

  return (
    <CoveragesLayout
      sideContent={
        <>
          <SectionData
            title="Current Coverages"
            coverages={data.currentCoverages}
            onSelect={(coverage) => setSelectedCoverage(coverage)}
            selectedCoverage={selectedCoverage}
          />
          <SectionData
            title="New Coverages"
            coverages={data.newCoverages}
            onSelect={(coverage) => setSelectedCoverage(coverage)}
            selectedCoverage={selectedCoverage}
          />
        </>
      }
    >
      {selectedCoverage ? (
        <BaseCard
          sxContent={{ display: "flex", flexDirection: "column", rowGap: 3 }}
          title={selectedCoverage.name}
        >
          {selectedCoverage.plans.map((plan, _, plans) => (
            <Box key={plan.name}>
              {plans.length > 1 ? (
                <Typography variant="body-large">
                  <strong>{plan.name}</strong>
                </Typography>
              ) : null}
              <Fields
                gridContainerProps={{ mt: 1 }}
                gridItemProps={{ size: { xs: 3 } }}
                data={plan.fields as FieldsData[]}
              />
            </Box>
          ))}
        </BaseCard>
      ) : null}
    </CoveragesLayout>
  );
}

function CoveragesLoading(): React.JSX.Element {
  return (
    <CoveragesLayout
      sideContent={
        <>
          <SectionLoading />
          <SectionLoading />
        </>
      }
    >
      <LoadingCard
        action={null}
        gridItemProps={{
          size: 3,
        }}
        skeleton={{
          count: 7,
          props: {
            height: 30,
          },
        }}
      />
    </CoveragesLayout>
  );
}

type CoveragesLayoutProps = PropsWithChildren<{
  sideContent: ReactNode;
}>;

function CoveragesLayout(props: CoveragesLayoutProps): React.JSX.Element {
  return (
    <Grid container spacing={3} columns={15}>
      <Grid size={3}>
        <BaseCard
          title="Coverages"
          sxContent={{
            paddingX: 0,
            paddingY: 1,
            "&:last-child": {
              paddingBottom: 1,
            },
          }}
        >
          <Box display="flex" flexDirection="column" gap={3}>
            {props.sideContent}
          </Box>
        </BaseCard>
      </Grid>
      <Grid size="grow">{props.children}</Grid>
    </Grid>
  );
}

type SectionProps = PropsWithChildren<{
  title: ReactNode;
}>;

function SectionList({ title, children }: SectionProps): React.JSX.Element {
  const isTitleStr = typeof title === "string";

  return (
    <List disablePadding={isTitleStr}>
      <ListSubheader disableGutters={!isTitleStr}>
        {isTitleStr ? (
          <Typography variant="body-medium">
            <strong>{title}</strong>
          </Typography>
        ) : (
          title
        )}
      </ListSubheader>
      {children}
    </List>
  );
}

function SectionLoading(): React.JSX.Element {
  const theme = useTheme();
  return (
    <SectionList
      title={<Skeleton variant="rectangular" height={theme.spacing(2.5)} />}
    >
      {Array.from({ length: 3 }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <ListItem disablePadding sx={{ marginTop: 1 }} key={index}>
          <Skeleton
            variant="rectangular"
            width="100%"
            height={theme.spacing(4.5)}
          />
        </ListItem>
      ))}
    </SectionList>
  );
}

function SectionData({
  title,
  coverages,
  selectedCoverage,
  onSelect,
}: {
  title: string;
  coverages: MappedCoverage[];
  selectedCoverage: MappedCoverage | null;
  onSelect: (coverage: MappedCoverage) => void;
}): React.JSX.Element | null {
  if (coverages.length === 0) return null;

  return (
    <SectionList title={title}>
      {coverages.map((coverage) => (
        <ListItem disablePadding key={coverage.id}>
          <ListItemButton
            onClick={() => onSelect(coverage)}
            selected={selectedCoverage?.id === coverage.id}
          >
            <ListItemText primary={coverage.name} />
          </ListItemButton>
        </ListItem>
      ))}
    </SectionList>
  );
}

export {
  CoveragesCard,
  Data as CoveragesCardData,
  Props as CoveragesCardProps,
};
